import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  DialogContentText,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { hasActiveModuleGrant } from '@realadvisor/constants';
import { EllipsisVerticalIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { GET_TEAM_MEMBER_EDIT_DETAILS } from '@modules/_shared/components/visibility-drawer/queries/agent-queries';
import {
  type VisibilityEntity,
  getSlugConstraintError,
  isAgentEntity,
} from '@modules/_shared/components/visibility-drawer/types';
import { useDrawerContext } from '@modules/_shared/components/visibility-drawer/VisibilityDrawer';
import { useIsTeamLeaderOrAdmin } from '@modules/_shared/hooks/useIsTeamLeaderOrAdmin';
import { generateAgentSlug } from '@modules/_shared/utils/agentSlug';
import {
  GET_USER,
  GET_USERS_LIST,
  GET_USERS_LIST_COUNT,
  GET_USER_ONBOARDING_HISTORY,
} from '@modules/users/userQueries';
import {
  PUBLISH_USER,
  UNPUBLISH_USER,
} from '@modules/visibility-team/visibilityTeamQueries';
import { MODULES_VISIBILITY } from '@shared/constants/module';
import { useLocale } from '@shared/hooks/locale';

type PublishMenuProps = {
  entity: VisibilityEntity;
};

const PublishMenu = ({ entity }: PublishMenuProps) => {
  const { t } = useLocale();
  const { teamId: urlTeamId } = useParams<{ teamId: string }>();
  const { contextId } = useDrawerContext();
  const teamId = contextId ?? urlTeamId;
  const isLeaderOrAdmin = useIsTeamLeaderOrAdmin(teamId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    'publish' | 'unpublish' | null
  >(null);

  const handleMutationError = (error: ErrorLike) => {
    // Refused by users_guard_agent_publication (hasura-crm 1793346928000): the
    // Publish button is offered on members whose membership is deactivated.
    const message = error.message.includes('AGENT_PUBLICATION_FORBIDDEN')
      ? t(
          'Only an admin or a leader of a team this agent is active in can publish their profile.',
        )
      : getSlugConstraintError(error, t);
    if (message) {
      setErrorMessage(message);
    } else {
      setErrorLike(error);
    }
  };

  // Publishing can also approve the agent's self-onboarding (a database trigger, same
  // update), so the user page's onboarding card and the users list refetch too.
  const mutationOptions = {
    refetchQueries: [
      GET_TEAM_MEMBER_EDIT_DETAILS,
      GET_USER,
      GET_USER_ONBOARDING_HISTORY,
      GET_USERS_LIST,
      GET_USERS_LIST_COUNT,
    ],
    onError: handleMutationError,
  };

  const [publishUser, { loading: publishLoading }] = useMutation(
    PUBLISH_USER,
    mutationOptions,
  );

  const [unpublishUser, { loading: unpublishLoading }] = useMutation(
    UNPUBLISH_USER,
    mutationOptions,
  );

  const agentSlug = isAgentEntity(entity)
    ? entity.agent_slug ||
      generateAgentSlug(entity.first_name ?? null, entity.last_name ?? null)
    : null;

  if (!isLeaderOrAdmin || !isAgentEntity(entity)) {
    return null;
  }

  const open = Boolean(anchorEl);
  const isPublished = !!entity.show_in_agency_pages;
  const loading = publishLoading || unpublishLoading;

  const hasVisibilityModule = hasActiveModuleGrant(
    entity.module_grants,
    MODULES_VISIBILITY,
  );
  const hasProfilePicture = entity.user_images.length > 0;
  const canPublish =
    !isPublished && hasVisibilityModule && !!agentSlug && hasProfilePicture;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePublishClick = () => {
    handleClose();
    setConfirmAction('publish');
  };

  const handleUnpublishClick = () => {
    handleClose();
    setConfirmAction('unpublish');
  };

  const handleConfirm = () => {
    if (confirmAction === 'publish') {
      if (!agentSlug) {
        return;
      }
      publishUser({
        variables: {
          userId: entity.id,
          agentSlug,
        },
      });
    } else if (confirmAction === 'unpublish') {
      unpublishUser({
        variables: { userId: entity.id },
      });
    }
    setConfirmAction(null);
  };

  const agentName = [entity.first_name, entity.last_name]
    .filter(Boolean)
    .join(' ');

  const disabledReasons = isPublished
    ? []
    : [
        !hasVisibilityModule && t('Visibility module required to publish'),
        !hasProfilePicture && t('Profile picture required to publish'),
        !agentSlug && t('Unable to generate a profile URL from name'),
      ].filter((reason): reason is string => typeof reason === 'string');

  return (
    <>
      <IconButton size="small" onClick={handleClick} disabled={loading}>
        <EllipsisVerticalIcon size={18} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Tooltip
          title={
            disabledReasons.length > 0
              ? disabledReasons.map(reason => <div key={reason}>{reason}</div>)
              : ''
          }
          placement="left"
        >
          <span>
            <MenuItem onClick={handlePublishClick} disabled={!canPublish}>
              {t('Publish')}
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem onClick={handleUnpublishClick} disabled={!isPublished}>
          {t('Unpublish')}
        </MenuItem>
      </Menu>
      {confirmAction != null && (
        <SlideDialog
          open
          title={
            confirmAction === 'publish'
              ? t('Publish profile')
              : t('Unpublish profile')
          }
          onClose={() => setConfirmAction(null)}
          onSave={handleConfirm}
          loading={loading}
          saveText={confirmAction === 'publish' ? t('Publish') : t('Unpublish')}
          saveIcon={null}
          maxWidth="xs"
        >
          <DialogContentText sx={{ p: 2 }}>
            {confirmAction === 'publish'
              ? t('Are you sure you want to publish {{name}}?', {
                  name: agentName,
                })
              : t('Are you sure you want to unpublish {{name}}?', {
                  name: agentName,
                })}
          </DialogContentText>
        </SlideDialog>
      )}
      {errorMessage != null && (
        <RASnackbar
          message={errorMessage}
          severity="error"
          onClose={() => setErrorMessage(null)}
        />
      )}
      {apolloError && (
        <MutationErrorSnackbar
          error={apolloError}
          onClose={() => setErrorLike(null)}
        />
      )}
    </>
  );
};

export default PublishMenu;
